import { NoteSearchRequestQuery, NoteSearchResponse } from '@lio/api-types';
import { NoteType } from '@lio/rule-engine';
import { Note } from '@models/note';
// Direct imports to avoid circular dependency through the service index barrel when this file is re-exported.
import { noteQueryKey } from './keys';
import { getFilterText } from './note.helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { buildQueryParamForStringOrNull } from '@utils/buildQueryParamForStringOrNull';
import { escapeSpecialChars } from '@utils/regex';

// TODO: remove & { includeDeleted?: boolean } once types are published

const getSearchNotesQuery = (params: NoteSearchRequestQuery) => {
  const {
    quoteId,
    policyId,
    pageLimit,
    pageOffset,
    customerId,
    type,
    brokerageId,
    parentId,
    sort,
    includeDeleted,
    isPinned,
    searchText,
  } = params;
  const query = new URLSearchParams();
  if (quoteId || quoteId === null) {
    buildQueryParamForStringOrNull(query, 'quoteId', quoteId);
  }
  if (policyId || policyId === null) {
    buildQueryParamForStringOrNull(query, 'policyId', policyId);
  }
  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }
  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }
  if (customerId) {
    query.append('customerId', customerId);
  }
  if (type) {
    query.append('type', type);
  }
  if (brokerageId) {
    query.append('brokerageId', brokerageId);
  }
  if (parentId) {
    query.append('parentId', parentId);
  }

  if (sort) {
    query.append('sort', sort.toString());
  }

  if (includeDeleted) {
    query.append('includeDeleted', 'true');
  }

  if (isPinned) {
    query.append('isPinned', 'true');
  }

  if (searchText) {
    query.append('searchText', searchText);
  }

  return query.toString();
};

function doFilterByNotes(note: Note, newValue?: string): boolean {
  if (!newValue) return true;
  const regex = new RegExp(escapeSpecialChars(newValue), 'gi');
  return !!note.filterText?.match(regex);
}

export const useSearchNotes = (params: NoteSearchRequestQuery, searchbarText?: string) => {
  const client = useClient();
  const query = getSearchNotesQuery(params);

  const result = useInfiniteQuery<NoteSearchResponse, Error>({
    queryKey: noteQueryKey.searchWithParams(params),
    queryFn: ({ pageParam = 0 }) =>
      client
        .get(`note?${query}&pageOffset=${pageParam}`)
        .json<NoteSearchResponse>()
        .then((response) => {
          return {
            ...response,
            items: response.items
              .map((n) => ({
                ...n,
                filterText: getFilterText({
                  body: n.body,
                  createdAtOrReplacedAt: new Date(n.createdAtOrReplacedAt).toISOString(),
                  artifacts: n.artifacts,
                }),
              }))
              .filter((n) => doFilterByNotes(n as Note, searchbarText)),
          };
        }),
    select: (data) => {
      const result = {
        pageParams: data.pageParams,
        pages: data.pages.map((page) => ({
          items: page.items.filter((n) => doFilterByNotes(n as Note, searchbarText)),
          pagination: page.pagination,
        })),
      };
      if (params.type === NoteType.Note) {
        return result;
      }
      return {
        ...result,
        pages: result.pages.map((page) => ({
          items: page.items,
          pagination: page.pagination,
        })),
      };
    },
    enabled: !!params.quoteId || !!params.customerId || !!params.brokerageId || !!params.policyId,
    staleTime: 20000,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.pagination) return undefined;
      if (!lastPage.pagination.pageLimit) return undefined;
      if (!lastPage.pagination.total) return undefined;
      const { total, pageLimit } = lastPage.pagination;
      const fetchedPageCount = allPages.length;
      const nextPageOffsetValue = fetchedPageCount * pageLimit;
      if (nextPageOffsetValue > total) return undefined;
      return nextPageOffsetValue;
    },
  });
  return { ...result, searchNotes: result.data };
};
